import { useState, useMemo, useEffect } from "react";
import {
  Button,
  ButtonGroup,
  H2,
  Modal,
  TextInput,
} from "@datapay-by-datacom/design-system";
import {
  LegislationWork,
  SourceOrgCategory,
  SourceType,
} from "../../../generated/ingest-api";
import { useCreateSource } from "../../hooks/OrgSource/useCreateSource";
import { useSearchLegislationWorks } from "../../hooks/OrgSource/useSearchLegislationWorks";
import { parseLegislationUrl } from "../../hooks/OrgSource/parseLegislationUrl";
import styles from "./CreateSourceModal.module.css";

type ModalView = "search" | "confirm" | "manual";

interface CreateSourceModalProps {
  category: SourceOrgCategory;
  orgId: string;
  isOpen: boolean;
  onClose: () => void;
}

function workIdToUrl(workId: string): string {
  const parts = workId.split("_");
  if (parts.length < 4) {
    throw new Error(`Unexpected work_id format: ${workId}`);
  }
  const [type, scope, year, number] = parts;
  return `https://www.legislation.govt.nz/${type}/${scope}/${year}/${number}/latest/`;
}

export function CreateSourceModal({
  category,
  orgId,
  isOpen,
  onClose,
}: CreateSourceModalProps) {
  const [view, setView] = useState<ModalView>("search");

  // Search view state
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<LegislationWork | null>(null);

  // Confirm view state (pre-filled from search result, editable)
  const [sourceName, setSourceName] = useState("");
  const [pollIntervalHours, setPollIntervalHours] = useState("24");

  // Manual view state
  const [manualSourceName, setManualSourceName] = useState("");
  const [legislationUrl, setLegislationUrl] = useState("");
  const [versionDate, setVersionDate] = useState("");
  const [manualPollIntervalHours, setManualPollIntervalHours] = useState("24");

  const createMutation = useCreateSource(category, orgId);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const searchQuery = useSearchLegislationWorks(debouncedSearch);

  // Manual form derived values
  const parsedUrl = useMemo(
    () => parseLegislationUrl(legislationUrl),
    [legislationUrl]
  );

  const versionId = useMemo(() => {
    if (!parsedUrl || !versionDate) return null;
    return `${parsedUrl.workId}_en_${versionDate}`;
  }, [parsedUrl, versionDate]);

  const manualPollHours = Number(manualPollIntervalHours);
  const isManualValid =
    manualSourceName.trim() !== "" &&
    parsedUrl !== null &&
    versionDate !== "" &&
    !Number.isNaN(manualPollHours) &&
    manualPollHours >= 1;

  const confirmPollHours = Number(pollIntervalHours);
  const isConfirmValid =
    sourceName.trim() !== "" &&
    !Number.isNaN(confirmPollHours) &&
    confirmPollHours >= 1;

  const handleSelectResult = (work: LegislationWork) => {
    setSelected(work);
    setSourceName(work.latest_matching_version?.title ?? "");
    setPollIntervalHours("24");
    setView("confirm");
  };

  const handleConfirmCreate = () => {
    if (!isConfirmValid || !selected?.latest_matching_version) return;

    const workId = selected.work_id;
    createMutation.mutate(
      {
        source_type: SourceType.legislation_nz_pco,
        source_name: sourceName.trim(),
        source_url: workIdToUrl(workId),
        legislation_work_id: workId,
        legislation_version_id: selected.latest_matching_version.version_id,
        poll_interval_hours: Number(pollIntervalHours),
        org_category: category,
        org_id: orgId,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleManualSubmit = () => {
    if (!isManualValid || !parsedUrl || !versionId) return;

    createMutation.mutate(
      {
        source_type: SourceType.legislation_nz_pco,
        source_name: manualSourceName.trim(),
        source_url: legislationUrl.trim(),
        legislation_work_id: parsedUrl.workId,
        legislation_version_id: versionId,
        poll_interval_hours: Number(manualPollIntervalHours),
        org_category: category,
        org_id: orgId,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    setView("search");
    setSearchInput("");
    setDebouncedSearch("");
    setSelected(null);
    setSourceName("");
    setPollIntervalHours("24");
    setManualSourceName("");
    setLegislationUrl("");
    setVersionDate("");
    setManualPollIntervalHours("24");
    onClose();
  };

  const results = searchQuery.data?.results ?? [];
  const hasResults = results.length > 0;

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.modalContent}>
        <H2 className={styles.title}>Add Document</H2>

        {view === "search" && (
          <div className={styles.form}>
            <div className={styles.field}>
              <TextInput
                label="Search Legislation"
                value={searchInput}
                onChange={setSearchInput}
                placeholder="e.g. Holidays Act"
              />
            </div>

            <div
              className={styles.results}
              role="listbox"
              aria-label="Search results"
            >
              <div aria-live="polite" aria-atomic="true">
                {searchQuery.isFetching && (
                  <p className={styles.statusMessage}>Searching...</p>
                )}
                {searchQuery.isError && (
                  <p className={styles.statusMessage}>
                    Search failed. Please try again.
                  </p>
                )}
                {!searchQuery.isFetching &&
                  !searchQuery.isError &&
                  debouncedSearch &&
                  !hasResults && (
                    <p className={styles.statusMessage}>No results found.</p>
                  )}
              </div>
              {hasResults &&
                results.map((work) =>
                  work.latest_matching_version ? (
                    <button
                      type="button"
                      role="option"
                      key={work.work_id}
                      className={styles.resultItem}
                      onClick={() => handleSelectResult(work)}
                    >
                      <span className={styles.resultTitle}>
                        {work.latest_matching_version.title}
                      </span>
                      <span className={styles.resultMeta}>
                        {[work.legislation_type, work.legislation_status]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                  ) : null
                )}
            </div>

            <div className={styles.actions}>
              <div className={styles.actionRow}>
                <button
                  type="button"
                  className={styles.toggleLink}
                  onClick={() => setView("manual")}
                >
                  Enter details manually
                </button>
                <Button variant="tertiary" onPress={handleClose}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {view === "confirm" && selected?.latest_matching_version && (
          <div className={styles.form}>
            <div className={styles.selectedPreview}>
              <span className={styles.previewRow}>
                <span className={styles.previewLabel}>Work ID</span>
                <span className={styles.previewValue}>{selected.work_id}</span>
              </span>
              <span className={styles.previewRow}>
                <span className={styles.previewLabel}>Version ID</span>
                <span className={styles.previewValue}>
                  {selected.latest_matching_version.version_id}
                </span>
              </span>
            </div>

            <div className={styles.field}>
              <TextInput
                label="Legislation Name"
                value={sourceName}
                onChange={setSourceName}
                placeholder="e.g. Holidays Act 2003"
              />
            </div>

            <div className={styles.field}>
              <TextInput
                label="Poll Interval (hours)"
                value={pollIntervalHours}
                onChange={setPollIntervalHours}
                placeholder="24"
              />
              {(Number.isNaN(confirmPollHours) || confirmPollHours < 1) && (
                <span className={styles.fieldError}>
                  Enter a valid number (minimum 1 hour)
                </span>
              )}
            </div>

            <div className={styles.actions}>
              <ButtonGroup>
                <Button
                  variant="tertiary"
                  onPress={() => setView("search")}
                >
                  Back
                </Button>
                <Button variant="tertiary" onPress={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onPress={handleConfirmCreate}
                  isDisabled={!isConfirmValid || createMutation.isPending}
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )}

        {view === "manual" && (
          <div className={styles.form}>
            <div className={styles.field}>
              <TextInput
                label="Legislation Name"
                value={manualSourceName}
                onChange={setManualSourceName}
                placeholder="e.g. Holidays Act 2003"
              />
            </div>

            <div className={styles.field}>
              <TextInput
                label="Legislation URL"
                value={legislationUrl}
                onChange={setLegislationUrl}
                placeholder="https://www.legislation.govt.nz/..."
              />
              {legislationUrl && !parsedUrl && (
                <span className={styles.fieldError}>
                  Invalid legislation URL format
                </span>
              )}
              {parsedUrl && (
                <span className={styles.workIdPreview}>
                  Work ID: {parsedUrl.workId}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="versionDate">
                Version Date
              </label>
              <input
                id="versionDate"
                type="date"
                value={versionDate}
                onChange={(e) => setVersionDate(e.target.value)}
              />
              {versionId && (
                <span className={styles.fieldHint}>
                  Version ID: {versionId}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <TextInput
                label="Poll Interval (hours)"
                value={manualPollIntervalHours}
                onChange={setManualPollIntervalHours}
                placeholder="24"
              />
              {(Number.isNaN(manualPollHours) || manualPollHours < 1) && (
                <span className={styles.fieldError}>
                  Enter a valid number (minimum 1 hour)
                </span>
              )}
            </div>

            <div className={styles.actions}>
              <div className={styles.actionRow}>
                <button
                  type="button"
                  className={styles.toggleLink}
                  onClick={() => setView("search")}
                >
                  ← Search legislation
                </button>
                <ButtonGroup>
                  <Button variant="tertiary" onPress={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onPress={handleManualSubmit}
                    isDisabled={!isManualValid || createMutation.isPending}
                  >
                    {createMutation.isPending ? "Creating..." : "Create"}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
